export interface ErrorLogData {
  message: string;
  stack?: string;
  url: string;
  context?: string;
  timestamp: string;
  userId?: string;
}

export interface ErrorContext {
  action?: string;
  component?: string;
  metadata?: Record<string, any>;
}

class UnifiedErrorLogger {
  private static instance: UnifiedErrorLogger;

  static getInstance(): UnifiedErrorLogger {
    if (!UnifiedErrorLogger.instance) {
      UnifiedErrorLogger.instance = new UnifiedErrorLogger();
    }
    return UnifiedErrorLogger.instance;
  }

  async logError(error: Error, context?: ErrorContext): Promise<void> {
    try {
      const errorData: ErrorLogData = {
        message: error.message,
        stack: error.stack,
        url: window.location.href,
        context: context ? JSON.stringify(context) : undefined,
        timestamp: new Date().toISOString(),
        userId: undefined,
      };

      // Log to console in development
      if (import.meta.env.DEV) {
        console.error('Error logged:', errorData);
      }

      // Send to server-side error logging API (non-blocking)
      try {
        const token = localStorage.getItem('auth_token');
        fetch('/api/analytics/track', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ event: 'error', properties: errorData }),
        }).catch(() => {
          // Non-blocking — fall back to localStorage
          this.fallbackToLocalStorage(errorData);
        });
      } catch {
        this.fallbackToLocalStorage(errorData);
      }
    } catch (loggingError) {
      console.error('Critical error in logging system:', loggingError);
      this.fallbackToLocalStorage({
        message: error.message,
        stack: error.stack,
        url: window.location.href,
        context: context ? JSON.stringify(context) : undefined,
        timestamp: new Date().toISOString(),
        userId: undefined,
      });
    }
  }

  private fallbackToLocalStorage(errorData: ErrorLogData): void {
    try {
      const storedErrors = JSON.parse(localStorage.getItem('pendingErrors') || '[]');
      storedErrors.push(errorData);
      localStorage.setItem('pendingErrors', JSON.stringify(storedErrors.slice(-50)));
    } catch (storageError) {
      console.error('Failed to store error in localStorage:', storageError);
    }
  }

  async syncPendingErrors(): Promise<void> {
    try {
      const pendingErrors = JSON.parse(localStorage.getItem('pendingErrors') || '[]');
      if (pendingErrors.length === 0) return;

      const token = localStorage.getItem('auth_token');
      for (const errorData of pendingErrors) {
        try {
          await fetch('/api/analytics/track', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ event: 'error', properties: errorData }),
          });
        } catch {
          // Will retry on next sync
        }
      }

      localStorage.removeItem('pendingErrors');
    } catch (error) {
      console.error('Failed to sync pending errors:', error);
    }
  }
}

export const errorLogger = UnifiedErrorLogger.getInstance();
